import React, { useRef, useState } from "react";
import { Stack, Typography } from "@mui/material";

import { StyledDragnDrop } from "../HubHookFormFileInput/styles";
import { useHubSnackBar } from "../../hooks";
import { usePostAttachment } from "../../hooks";

import { HubIcon } from "../HubIcon";
import { HubButton } from "../inputs";

import { getCategoryRequirements } from "../../constants/attachments";
import AttachmentMetaDialog from "./AttachmentMetaDialog";

// Drag-and-drop file uploader. Two modes:
//   • Single-category: pass `category` — files upload directly when all
//     required metadata is satisfied by `metadataDefaults`, otherwise the
//     dialog opens to collect the missing fields.
//   • Multi-category:  pass `categoryOptions={[id, id, ...]}` — the dialog
//     opens for every drop so the user can pick the file type, then any
//     per-category metadata is collected in the same dialog.
const AttachmentUploader = ({
  resourceType,
  resourceId,
  category,
  categoryOptions,
  label = "Drag and Drop",
  accept,
  disabled,
  metadataDefaults = {},
}) => {
  const inputRef = useRef(null);
  const inputId = useRef(
    `attach-${resourceType}-${
      category ?? "multi"
    }-${Math.random().toString(36).slice(2, 9)}`,
  );
  const [hovering, setHovering] = useState(false);
  const [pending, setPending] = useState(null); // { file }

  const { mutate: upload, isLoading } = usePostAttachment();
  const { addSnackbarError } = useHubSnackBar();

  const isDisabled = disabled || isLoading || !resourceId;
  const isMulti = !!categoryOptions?.length;

  const startUpload = (file, dialogPayload = {}) => {
    const finalCategory = dialogPayload.category ?? category;
    upload({
      file,
      resourceType,
      resourceId,
      category: finalCategory,
      ...metadataDefaults,
      ...dialogPayload,
    });
  };

  const handleFiles = (files = []) => {
    if (!files.length) return;
    if (isDisabled) {
      if (!resourceId) {
        addSnackbarError({
          message: "Save the form first to enable file uploads.",
        });
      }
      return;
    }

    files.forEach((file) => {
      // Multi-category mode: always open the dialog so the user picks the
      // category (which may also reveal required metadata fields).
      if (isMulti) {
        setPending((current) => current || { file });
        return;
      }

      // Single-category mode: open the dialog only if metadata is missing.
      const requirements = getCategoryRequirements(category);
      const missing = requirements.filter((field) => {
        const v = metadataDefaults?.[field];
        return v == null || v === "";
      });
      if (missing.length) {
        setPending((current) => current || { file });
      } else {
        startUpload(file);
      }
    });
  };

  const handleDialogConfirm = (values) => {
    if (pending) startUpload(pending.file, values);
    setPending(null);
  };

  const handleDialogCancel = () => setPending(null);

  const handleInput = (e) => {
    handleFiles(Array.from(e.target.files || []));
    e.target.value = ""; // re-pick same file allowed
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (isDisabled) return;
    e.dataTransfer.dropEffect = "copy";
    setHovering(true);
  };

  const handleDragLeave = () => setHovering(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setHovering(false);
    if (isDisabled) return;
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  return (
    <>
      <StyledDragnDrop
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        hovering={hovering}
        sx={{
          opacity: isDisabled ? 0.6 : 1,
          cursor: isDisabled ? "default" : "pointer",
          padding: "16px 20px",
        }}
        onClick={() => !isDisabled && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          id={inputId.current}
          type="file"
          accept={accept}
          multiple
          style={{ display: "none" }}
          onChange={handleInput}
        />
        <Stack gap="6px" alignItems="center">
          <HubIcon
            icon={isLoading ? "Clock" : "Upload"}
            size="14px"
            sx={{ color: "hubColors.mainFocus" }}
          />
          <Typography variant="greyDark12Bold" align="center" fontSize={12}>
            {isLoading ? "Uploading…" : label}
          </Typography>
          <HubButton
            variant="text"
            size="xsmall"
            component="label"
            htmlFor={inputId.current}
            icon="Share"
            iconProps={{ size: "10px" }}
            label="Browse Device"
            disabled={isDisabled}
            removePreventDefault
            onClick={(e) => e.stopPropagation()}
          />
          {!resourceId && (
            <Typography variant="caption" color="text.secondary" fontSize={10}>
              Save first to enable uploads
            </Typography>
          )}
        </Stack>
      </StyledDragnDrop>

      <AttachmentMetaDialog
        open={!!pending}
        category={category}
        categoryOptions={categoryOptions}
        fileName={pending?.file?.name}
        defaults={metadataDefaults}
        onCancel={handleDialogCancel}
        onConfirm={handleDialogConfirm}
      />
    </>
  );
};

export default AttachmentUploader;
